<template>
  <div>
    <h1 class="text-black text-xl md:text-2xl font-bold capitalize">
      {{ businessDetails.name || '' }}
    </h1>
    <div v-if="!isBusinessTypeHNI" class="tags">
      <span v-if="businessDetails.websiteUrl">{{
        businessDetails.websiteUrl || ''
      }}</span>
      <span class="uppercase">{{ businessDetails.businessType || '' }}</span>
    </div>

    <p class="mt-2 text-gray-muted">
      Verify {{ isBusinessTypeHNI ? 'Your' : 'Business' }} Information
    </p>

    <div class="pt-8 pb-12 flex flex-col gap-6">
      <div
        class="kyc-stage border border-border-10 hover:border-gray-2 rounded-2xl flex justify-between items-center p-4 md:p-7 bg-white cursor-pointer"
        v-for="item in isBusinessTypeHNI ? hniKycDetails : kycDetails"
        :key="item.step"
        @click="handleNext(item.step)"
      >
        <div class="w-11/12 flex gap-4 justify-between">
          <span class="w-1/12">
            <component :is="getIconType(item)" size="h-8 w-8" />
          </span>
          <div class="w-10/12 md:w-11/12">
            <h3 class="text-base md:text-lg font-bold text-black-10">
              {{ item.title }}
            </h3>

            <div
              v-if="kycStage(item.step) === 'ongoing'"
              class="w-full flex items-center gap-4 mt-2"
            >
              <div class="w-8/12">
                <el-progress
                  class="w-full"
                  :stroke-width="16"
                  :percentage="calculatePercentage(item.step)"
                >
                </el-progress>
              </div>
              <span class="text-xs md:text-sm text-gray-muted"
                >{{ completedItemLength(item.step) }}/{{
                  item.totalItems
                }}
                Completed</span
              >
            </div>

            <p
              v-else
              class="w-full flex justify-between text-xs md:text-sm text-gray-muted mt-1"
            >
              <span
                :class="
                  kycStage(item.step) === 'inDispute' ? 'w-10/12' : 'w-full'
                "
              >
                {{ item.content }}
              </span>

              <span
                v-if="kycStage(item.step) === 'inDispute'"
                class="font-medium"
              >
                <strong class="text-error-20">{{
                  errorsLength(item.step)
                }}</strong>
                Error{{ errorsLength(item.step) > 1 ? 's' : '' }}
              </span>
            </p>
          </div>
        </div>

        <arrow-icon color="#5D5E68" size="h-7 w-7" />
      </div>

      <p
        class="text self-center my-2 text-xs md:text-sm text-center text-gray-muted"
      >
        All information confirmed. Click "Submit" to submit your KYC
      </p>

      <button
        class="submitBtn self-center w-44 h-12 md:w-52 md:h-14 rounded-lg"
        :class="isLoading && 'cursor-default pointer-events-none'"
        :disabled="!isFormCompleted"
        @click="submitKycFinalStage"
      >
        <spinner v-if="isLoading" size="small" />
        <span v-else>Submit</span>
      </button>
    </div>
  </div>
</template>

<script>
import {
  KYC_INFO,
  HNI_KYC_INFO,
  BUSINESS_INFO,
  DIRECTORS_INFO,
  UPLOAD_INFO,
  SME_UPLOAD_INFO,
  HNI_BUSINESS_INFO,
  HNI_DIRECTORS_INFO,
  HNI_UPLOAD_INFO
} from '@/utils/constants'
import kyc from '@/mixins/kyc.js'
import { gsap } from 'gsap'

export default {
  mixins: [kyc],
  components: {
    ArrowIcon: () => import('@/assets/v2/icons/ArrowRightIcon.vue'),
    CompletedIcon: () => import('@/assets/v2/icons/CheckMarkIcon.vue'),
    BusinessIcon: () => import('@/assets/v2/icons/BusinessIcon.vue'),
    UserIcon: () => import('@/assets/v2/icons/UserIcon.vue'),
    AddUserIcon: () => import('@/assets/v2/icons/AddUserIcon.vue'),
    UploadIcon: () => import('@/assets/v2/icons/UploadDocumentIcon.vue'),
    DisputeIcon: () => import('@/assets/v2/icons/AlertCircle.vue'),
    Spinner: () => import('@/components/v2/app/Spinner.vue')
  },
  data () {
    return {
      isLoading: false,
      kycDetails: KYC_INFO,
      hniKycDetails: HNI_KYC_INFO,
      businessInfo: BUSINESS_INFO,
      directorsInfo: DIRECTORS_INFO,
      uploadInfo: UPLOAD_INFO,
      smeUploadInfo: SME_UPLOAD_INFO,
      hniBusinessInfo: HNI_BUSINESS_INFO,
      hniDirectorsInfo: HNI_DIRECTORS_INFO,
      hniUploadInfo: HNI_UPLOAD_INFO
    }
  },
  computed: {
    businessDetails () {
      return this.$store.state.auth?.business || {}
    },
    isFormCompleted () {
      const requiredStages = this.isBusinessTypeHNI ? 4 : 6

      return (
        this.submittedStages.length === requiredStages &&
        this.mandatoryDocuments.every((doc) =>
          this.submittedDocuments.includes(doc)
        )
      )
    },
    documents () {
      const docs = this.isBusinessTypeHNI ? this.hniUploadInfo : this.uploadInfo
      return docs.map((item) => item.document)
    },
    mandatoryDocuments () {
      const docs = this.isBusinessTypeHNI
        ? this.hniUploadInfo
        : this.isBusinessTypeSME
        ? this.smeUploadInfo
        : this.uploadInfo

      return docs
        .filter((item) => item.isCompulsory)
        .map((item) => item.document)
    }
  },
  methods: {
    handleNext (step) {
      const routes = [
        '/submit-kyc/business-information',
        '/submit-kyc/director-information',
        '/submit-kyc/upload-document'
      ]

      this.$router.push(routes[step - 1])
    },
    getIconType (item) {
      const stage = this.kycStage(item.step)
      return stage === 'completed'
        ? 'completed-icon'
        : stage === 'inDispute'
        ? 'dispute-icon'
        : item.icon
    },
    errorsLength (step) {
      const items = this.getItemsByStep(step)

      const stagesInDispute =
        step === 3 ? this.disputedDocuments : this.disputedStages
      return step === 3
        ? stagesInDispute.length
        : items.filter((item) => stagesInDispute.includes(item.step)).length
    },
    completedItemLength (step) {
      const items = this.getItemsByStep(step)
      return step === 3
        ? this.submittedDocuments.length
        : items.filter((item) => this.submittedStages.includes(item.step))
            .length
    },
    calculatePercentage (step) {
      const items = this.getItemsByStep(step)
      return step === 3
        ? (this.submittedDocuments.length / this.documents.length) * 100
        : (items.filter((item) => this.submittedStages.includes(item.step))
            .length /
            items.length) *
            100
    },
    // This function determines the stage of the kyc process
    // for each section. If the stage is in 'start', 'ongoing', 'completed'
    // or 'inDispute'.
    kycStage (stepNumber) {
      const stepConfig = this.isBusinessTypeHNI
        ? {
            1: { mandatoryStages: 2, stages: [1, 2] },
            2: { mandatoryStages: 2, stages: [3, 4] },
            3: { mandatoryStages: 4, stages: this.mandatoryDocuments }
          }
        : {
            1: { mandatoryStages: 4, stages: [1, 2, 3, 4] },
            2: { mandatoryStages: 2, stages: [5, 6] },
            3: {
              mandatoryStages: this.isBusinessTypeSME ? 8 : 10,
              stages: this.mandatoryDocuments
            }
          }

      const step = stepConfig[stepNumber]
      const relevantStages =
        stepNumber === 3 ? this.submittedDocuments : this.submittedStages
      const stagesInDispute =
        stepNumber === 3 ? this.disputedDocuments : this.disputedStages

      const disputeCount = stagesInDispute.filter((stage) =>
        step.stages.includes(stage)
      ).length
      const completedCount = relevantStages.filter((doc) =>
        step.stages.includes(doc)
      ).length

      if (disputeCount > 0) {
        return 'inDispute'
      } else if (completedCount === 0) {
        return 'start'
      } else if (completedCount < step.mandatoryStages) {
        return 'ongoing'
      } else {
        return 'completed'
      }
    }
  },
  mounted () {
    gsap.context(() => {
      gsap.fromTo(
        '.kyc-stage',
        {
          x: -150,
          opacity: 0
        },
        {
          x: 0,
          stagger: 0.2,
          opacity: 1
        }
      )
      gsap.fromTo(
        '.text, .submitBtn',
        {
          x: -150,
          opacity: 0
        },
        {
          x: 0,
          delay: 0.7,
          opacity: 1
        }
      )
    })
  },
  unmounted () {
    gsap.revert()
  }
}
</script>

<style scoped>
.tags {
  display: flex;
  gap: 8px;
  margin-top: 5px;
}

.tags span {
  padding: 6px 16px;
  border-radius: 100px;
  background-color: #f5f6ff;
  color: #5d5e68;
  font-size: 12px;
}

.submitBtn {
  background-color: var(--color-primary);
  color: white;
}

.submitBtn:disabled {
  background-color: #e0e0e0;
  color: #757575;
}

>>> .el-progress-bar__inner {
  background: var(--color-primary);
}
>>> .el-progress-bar {
  width: 115%;
}
>>> .el-progress__text {
  display: none;
}
</style>
